import MojitoCore from 'mojito/core';
import MojitoModules from 'mojito/modules';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';

const EventUtils = MojitoServices.SportsContent.Events.utils;
const SportsMenu = MojitoModules.SportsMenu.module;
const QuickEventsNavigation = MojitoModules.QuickEventsNavigation;
const EventVisualisationSkeleton = MojitoModules.EventVisualisationSkeleton;
const EventNotificationSubscriber = MojitoModules.EventNotifications.EventNotificationSubscriber;
const AnalyticsContext = MojitoCore.Presentation.AnalyticsContext.Context;
const AnalyticsContextBuilder = MojitoCore.Presentation.AnalyticsContext.ContextBuilder;

const { EventMarkets, Visualisation } = MojitoModules;

const {
    FlexPane,
    DocumentHead,
    EventDisclaimer,
    GlobalLinks,
    PageHeader,
    PageSplitLayout,
    DefaultHeadMetaFactory,
    LoaderSuspense,
    EmptyContent,
    QuickNavigationButton,
    QuickNavigationPane,
} = MojitoPresentation.Components;

const { useEventItem, useEventItemUnavailability } = MojitoModules.Common.Hooks;
const {
    actions: userSettingsActions,
    types: UserSettingsTypes,
    selectors: userSettingsSelectors,
} = MojitoServices.UserSettings;
const { selectOnboarding } = userSettingsSelectors;
const { EVENTS_QUICK_NAVIGATION } = UserSettingsTypes.ONBOARDING_TYPES;

const analyticsContext = new AnalyticsContextBuilder()
    .withAnalyticsPath('Event Detail page')
    .build();

const onPageHeaderBackClicked = () => window.history.back();

const isInPlayEvent = event => Boolean(event.details.inPlay);

const EventPage = props => {
    const { mojitoTools } = props;
    const { config } = mojitoTools;
    const { eventId } = useParams();
    const eventItem = useEventItem(eventId);
    const isEventItemUnavailable = useEventItemUnavailability(eventId);
    const commonProps = { ...props, eventItem };

    return (
        <>
            <EventDocumentHead {...commonProps} />
            <PageSplitLayout>
                {isEventItemUnavailable ? (
                    <EmptyContent config={config.emptyContent} />
                ) : (
                    <>
                        <EventPageHeader {...commonProps} />
                        <EventContent {...commonProps} />
                    </>
                )}
                <SecondaryContent {...commonProps} />
            </PageSplitLayout>
        </>
    );
};

const EventContent = props => {
    const { eventItem, mojitoTools } = props;
    const { config } = mojitoTools;

    return (
        <FlexPane config={config.eventContainer}>
            <VisualisationContent {...props} />
            <EventMarkets config={config.eventMarkets} eventId={eventItem?.id} />
            {eventItem && (
                <EventDisclaimer sportId={eventItem.sportId} isInPlay={eventItem.details.inPlay} />
            )}
        </FlexPane>
    );
};

const VisualisationContent = props => {
    const { mojitoTools, eventItem } = props;
    const { config } = mojitoTools;

    const skeleton = <EventVisualisationSkeleton config={config.visualisationSkeleton} />;

    return (
        <LoaderSuspense
            config={config.visualisationSkeletonLoader}
            isContentPending={!eventItem}
            loader={skeleton}
        >
            <FlexPane config={config.visualisationContainer}>
                {eventItem && <Visualisation event={eventItem} />}
            </FlexPane>
        </LoaderSuspense>
    );
};

const EventNotificationButton = ({ eventId }) => (
    <AnalyticsContext.Provider value={analyticsContext}>
        <EventNotificationSubscriber eventId={eventId} />
    </AnalyticsContext.Provider>
);

const SecondaryContent = props => {
    const { eventItem, mojitoTools } = props;
    const { config } = mojitoTools;
    const { sportId } = eventItem || {};

    return (
        <FlexPane config={config.secondaryContentContainer}>
            <SportsMenu selectedSportId={sportId} />
            <GlobalLinks />
        </FlexPane>
    );
};

const EventDocumentHead = props => {
    const { eventItem, mojitoTools } = props;
    const { config, appContext, l10n } = mojitoTools;

    if (!eventItem) {
        return null;
    }

    const isInPlay = isInPlayEvent(eventItem);

    const metaFactory = config.documentHeadMetaFactory || DefaultHeadMetaFactory;
    const documentHeadMetaFactory = new metaFactory(l10n);
    const { name, id, className, classId, sportId, typeName, typeId, startTime } = eventItem;
    const sportName = sportId && appContext.sportName(sportId);
    const key = isInPlay ? 'INPLAY' : 'PREPLAY';

    const headData = {
        title: `$EVENT_PAGE.PAGE_TITLE.${key}`,
        keywords: `$EVENT_PAGE.PAGE_KEYWORDS.${key}`,
        description: `$EVENT_PAGE.PAGE_DESCRIPTION.${key}`,
        canonical: `$EVENT_PAGE.PAGE_CANONICAL.${key}`,
        localizedParams: new Map([
            ['sportName', sportName],
            ['eventName', name],
            ['eventClass', className],
            ['eventType', typeName],
            ['eventStartTime', startTime],
        ]),
        params: {
            sportId,
            eventId: id,
            eventClassId: classId,
            eventClassType: typeId,
        },
    };

    return <DocumentHead data={headData} metaFactory={documentHeadMetaFactory} />;
};

const EventPageHeader = props => {
    const dispatch = useDispatch();
    const { isInplayPage, eventItem, mojitoTools } = props;
    const onboarding = useSelector(state => selectOnboarding(state));
    const {
        config,
        stringResolver,
        appContext: { analyticsEmitter },
    } = mojitoTools;
    const { [EVENTS_QUICK_NAVIGATION]: quickNavigationOnboarding } = onboarding;
    const [isQuickNavigationPopupOpen, setIsQuickNavigationPopupOpen] = useState(false);

    if (config.pageHeader.hide) {
        return null;
    }
    const closePopup = () => {
        analyticsEmitter.emitAnalytics('eventsQuickNavigationClosed');
        setIsQuickNavigationPopupOpen(false);
    };

    const onTooltipClick = () => {
        dispatch(
            userSettingsActions.updateOnboardingProgress({
                [EVENTS_QUICK_NAVIGATION]: { passed: true },
            })
        );
    };

    const {
        sportId,
        typeId,
        id: eventId,
        className,
        typeName,
        name: eventName,
        eventType,
        details: eventDetails,
    } = eventItem || {};

    const onQuickNavigationClick = () => {
        setIsQuickNavigationPopupOpen(true);
        analyticsEmitter.emitAnalytics('eventsQuickNavigationOpened', {
            sportId,
            eventType,
            eventName,
            isInPlay: eventDetails?.inPlay,
        });

        if (!quickNavigationOnboarding?.passed) {
            onTooltipClick();
        }
    };

    const isOutrightPage = EventUtils.isOutrightEventType(eventType);
    const tooltipText =
        !quickNavigationOnboarding?.passed && !isOutrightPage
            ? stringResolver.resolveString('$EVENT_PAGE.QUICK_NAVIGATION_TOOLTIP')
            : undefined;

    return (
        <FlexPane config={config.pageHeaderContainer}>
            {eventItem && (
                <PageHeader
                    showBackButton={true}
                    config={config.pageHeaderConfig}
                    onBackClicked={onPageHeaderBackClicked}
                    tooltipText={tooltipText}
                    onTooltipClick={onTooltipClick}
                >
                    <QuickNavigationButton
                        disabled={!eventDetails?.inPlay && isOutrightPage}
                        config={config.quickNavigationButton}
                        title={stringResolver.resolveAndFormatString(
                            '$EVENT_PAGE.QUICK_NAVIGATION_BUTTON_TITLE',
                            className,
                            typeName
                        )}
                        subTitle={eventName}
                        onClick={onQuickNavigationClick}
                    />

                    <QuickNavigationPane
                        config={config.quickNavigationPane}
                        isEnabled={isQuickNavigationPopupOpen}
                        onOutsideClick={closePopup}
                    >
                        <QuickEventsNavigation
                            config={config.quickEventsNavigation}
                            sportId={sportId}
                            typeId={typeId}
                            isInPlay={isInplayPage}
                            selectedItemId={eventId}
                            onItemClick={closePopup}
                        />
                    </QuickNavigationPane>

                    <EventNotificationButton eventId={eventItem.id} />
                </PageHeader>
            )}
        </FlexPane>
    );
};

export default EventPage;
